// Copyright (c) 2025 by Audere

import React, { useEffect, useState } from "react";
import { useQCConversations } from "../hooks/useQCConversations";
import Sidebar from "./Sidebar";
import MainPanel from "./MainPanel";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LabelingStatus,
  QCConversationData,
} from "../features/api/labelApiSlice";
import {
  setConvoLabelers,
  setLabeler,
  setQC,
} from "../features/slices/labelSlice";
import { ConvoLabelers } from "./LabelerSelect";
import { useAppDispatch, useAppSelector } from "../features/store";

import { useLabels } from "../hooks/useLabels";
import { selectCurrentEmail } from "../features/slices/authSlice";
import { useLabelingSchema } from "../hooks/useSchema";
import { useConversationForQC } from "../hooks/useConversationForQC";
import { conversationLimit } from "../constants/conversation";

const QCConversations: React.FC = () => {
  const { conversationUID, labelTypeID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allConversations, setAllConversations] = useState<
    QCConversationData[]
  >([]);
  const pageParam = searchParams.get("page");
  const pageValue =
    pageParam && !isNaN(Number(pageParam)) && Number(pageParam) > 0
      ? Number(pageParam)
      : 1;
  const [offsetValue, setOffsetValue] = useState(
    (pageValue - 1) * conversationLimit
  );
  const [hasMore, setHasMore] = useState(true);
  const { conversations, offset, endOfList, isLoading, error } =
    useQCConversations(labelTypeID ?? "", offsetValue);

  const { labels: existingLabels } = useLabels(conversationUID ?? "");
  const userEmail = useSelector(selectCurrentEmail);
  const dispatch = useAppDispatch();
  const {
    conversation: conversationMessages,
    isLoading: convoIsLoading,
    error: convoError,
  } = useConversationForQC(conversationUID ?? "", labelTypeID ?? "");

  const conversation = allConversations.find(
    (c) => c.conversation_uid === conversationUID
  );
  const convoLabelers = useAppSelector((state) => state.label.convoLabelers);
  const { labelingTasks, labelingTypes } = useLabelingSchema();

  useEffect(() => {
    if (conversations.length > 0) {
      setAllConversations(conversations);
    }
  }, [conversations]);

  useEffect(() => {
    if (endOfList && offset > 0) {
      setHasMore(true);
    } else {
      setHasMore(!endOfList);
    }
  }, [endOfList, offset]);

  const handleLoadMore = (value: "next" | "prev") => {
    if (!isLoading) {
      setAllConversations([]);
      let newOffset = offsetValue;

      if (value === "next") {
        newOffset = offsetValue + conversationLimit;
      } else {
        newOffset = Math.max(offsetValue - conversationLimit, 0);
      }

      const newPage = Math.floor(newOffset / conversationLimit) + 1;
      searchParams.set("page", String(newPage));
      setSearchParams(searchParams);
      setOffsetValue(newOffset);
      navigate({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
  };

  useEffect(() => {
    if (!pageParam || isNaN(Number(pageParam)) || Number(pageParam) <= 0) {
      searchParams.set("page", "1");
      setSearchParams(searchParams);
      navigate(
        { pathname: location.pathname, search: `?${searchParams.toString()}` },
        { replace: true }
      );
    }
  }, [location.pathname, navigate, searchParams, setSearchParams, pageParam]);
  useEffect(() => {
    if (allConversations.length > 0) {
      const initConvoLabelers = allConversations.reduce<ConvoLabelers>(
        (acc, convo) => {
          const labelers = convo.labelers.filter(
            (labeler) =>
              labeler.labeler_status === LabelingStatus.label_complete
          );
          const inProgressLabelers = convo.labelers.filter(
            (labeler) =>
              labeler.labeler_status === LabelingStatus.label_in_progress
          );
          const existing = convoLabelers?.[convo.conversation_uid];
          acc[convo.conversation_uid] = {
            labelers,
            selectedLabeler: existing?.selectedLabeler ?? labelers[0],
            inProgressLabelers,
          };
          return acc;
        },
        { ...(convoLabelers ?? {}) }
      );
      if (JSON.stringify(convoLabelers) !== JSON.stringify(initConvoLabelers)) {
        dispatch(setConvoLabelers(initConvoLabelers));
      }
    }
  }, [allConversations, dispatch, labelTypeID, convoLabelers]);
  useEffect(() => {
    if (conversationUID && convoLabelers) {
      const foundConversation = convoLabelers[conversationUID]
        ? conversation
        : conversationMessages;

      if (foundConversation) {
        const foundQC = foundConversation?.qc?.find(
          (qc) => qc.email === userEmail
        );
        const labelers = foundConversation.labelers.filter(
          (labeler) => labeler.labeler_status === LabelingStatus.label_complete
        );
        const selectedLabeler =
          convoLabelers[conversationUID]?.selectedLabeler || labelers[0];

        dispatch(setLabeler(selectedLabeler));
        dispatch(setQC(foundQC));
      }
    }
  }, [
    convoLabelers,
    conversationUID,
    dispatch,
    conversation,
    userEmail,
    labelTypeID,
    existingLabels,
    labelingTasks,
    labelingTypes,
    conversationMessages,
  ]);
  return (
    <>
      <Sidebar
        isLoading={isLoading}
        error={error}
        conversations={allConversations}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        endOfList={!!endOfList}
        conversationMessages={conversationMessages}
      />
      <MainPanel
        isLoading={isLoading}
        error={error}
        conversationMessages={conversationMessages}
        convoIsLoading={convoIsLoading}
        convoError={convoError}
      />
    </>
  );
};

export default QCConversations;
